/**
 * La marca Provalia, del kit de marca (provalia-brand-kit): el isotipo (la
 * estrella con su órbita) y el nombre en Outfit.
 *
 * Las imágenes están en public/brand, recortadas del kit. La guía pide la
 * versión en blanco sobre fondo oscuro, así que donde el fondo depende del
 * tema van las dos y la hoja de estilos enseña la que toca (.on-light /
 * .on-dark), también cuando el usuario fuerza un tema distinto al del sistema.
 */
type Tone = 'auto' | 'white';

function ThemedImg({ light, dark, tone, alt, className, height }: {
  light: string;
  dark: string;
  tone: Tone;
  alt: string;
  className: string;
  height: number;
}): JSX.Element {
  if (tone === 'white') return <img className={className} src={dark} alt={alt} height={height} />;
  return (
    <>
      <img className={`${className} on-light`} src={light} alt={alt} height={height} />
      <img className={`${className} on-dark`} src={dark} alt={alt} height={height} />
    </>
  );
}

/** El isotipo. La guía pone el mínimo en 24 px; por debajo, solo como sello. */
export function ProvaliaMark({ size = 28, tone = 'auto' }: { size?: number; tone?: Tone }): JSX.Element {
  return (
    <span className="pv-mark" aria-hidden="true">
      <ThemedImg light="/brand/icon-color.png" dark="/brand/icon-white.png" tone={tone} alt="" className="pv-mark-img" height={size} />
    </span>
  );
}

/** Isotipo y nombre en línea, para cabeceras y barras laterales. */
export function ProvaliaLogo({ size = 30, sub, tone = 'auto' }: { size?: number; sub?: string; tone?: Tone }): JSX.Element {
  return (
    <span className="pv-logo">
      <ProvaliaMark size={size} tone={tone} />
      <span className="pv-logo-text">
        <span className="pv-wordmark" style={{ fontSize: Math.round(size * 0.7) }}>
          Provalia
        </span>
        {sub ? <span className="pv-logo-sub">{sub}</span> : null}
      </span>
    </span>
  );
}

/** El logotipo completo (isotipo sobre el nombre), para las pantallas de entrada. */
export function ProvaliaLogoStacked({ height = 96 }: { height?: number }): JSX.Element {
  return (
    <ThemedImg
      light="/brand/logo-color.png"
      dark="/brand/logo-white.png"
      tone="auto"
      alt="Provalia"
      className="pv-logo-stacked"
      height={height}
    />
  );
}

/** «Funciona con Provalia», para el pie del portal de cada cliente. */
export function PoweredBy(): JSX.Element {
  return (
    <span className="subtle pv-powered">
      <ProvaliaMark size={24} />
      <span>
        Portal de proveedores con <strong className="pv-powered-name">Provalia</strong> · ECINSA
      </span>
    </span>
  );
}
